import { defineStore } from 'pinia';
import { IRoom, RoomTypeAvailability } from 'src/types/common';
import useRateHelperUtils from 'src/composables/rateHelperUtils';
import useUtils from 'src/composables/utils';
import { usePropertyStore } from './propertyStore';

const rateHelperUtils = useRateHelperUtils();
const utils = useUtils();

export const useRoomTypeAvailabilityStore = defineStore(
  'roomTypeAvailabilityStore',
  {
    state: () => ({
      RoomTypes: [] as RoomTypeAvailability[],
      availableRoomsPerRoomTypeId: null as Record<number, number> | null,
    }),
    getters: {
      totalAvailableRooms(): number {
        let total = 0;
        for (const key in this.availableRoomsPerRoomTypeId) {
          if (this.availableRoomsPerRoomTypeId.hasOwnProperty(key)) {
            const availableRooms = this.availableRoomsPerRoomTypeId[key];

            total = total + availableRooms;
          }
        }
        return total;
      },
      hasResults(): boolean {
        return this.RoomTypes.length > 0;
      },
    },
    actions: {
      getAvailableRoomTypesForActiveRoomSelection(
        roomData: IRoom,
        reservationItemRoomTypeId: number | null,
        preSelectedRoomTypeId: number | null,
      ): Array<RoomTypeAvailability> {
        const allRoomTypes = this.RoomTypes ?? [];

        if (roomData != null) {
          const availableRoomTypes = allRoomTypes.filter(
            (roomType: RoomTypeAvailability) => {
              const rates = roomType.Rates;

              const validRatePlans = rates.filter((u) => {
                let ratePlanOccupancyIsValid = true;
                if (u.UseOccupancyForRatePlan) {
                  ratePlanOccupancyIsValid = utils.checkIfOccupancyIsValid(
                    roomData.NumberOfAdults,
                    roomData.NumberOfChildren,
                    roomData.NumberOfInfants,
                    utils.getSmallestMaximumValue(
                      u.MaximumOccupancy,
                      roomType.RoomType.MaximumOccupancy,
                    ),
                    utils.getLargestMinimumValue(
                      u.MinimumOccupancy,
                      roomType.RoomType.MinimumOccupancy,
                    ),
                    utils.getSmallestMaximumValue(
                      u.AdultMaximumOccupancy,
                      roomType.RoomType.AdultMaximumOccupancy,
                    ),
                    utils.getSmallestMaximumValue(
                      u.ChildMaximumOccupancy,
                      roomType.RoomType.ChildMaximumOccupancy,
                    ),
                  );
                }

                return ratePlanOccupancyIsValid;
              });

              // Recalculate the lowest rate
              roomType.LowestRate = rateHelperUtils.calculateLowestRoomRate(
                roomData,
                validRatePlans,
                false,
              );
              roomType.OriginalLowestRate =
                rateHelperUtils.calculateLowestRoomRate(
                  roomData,
                  validRatePlans,
                  true,
                );

              if (
                reservationItemRoomTypeId != null &&
                reservationItemRoomTypeId == roomType.RoomType.RoomTypeId
              ) {
                return true;
              }

              const roomTypeMatch =
                preSelectedRoomTypeId == null ||
                (preSelectedRoomTypeId != null &&
                  preSelectedRoomTypeId == roomType.RoomType.RoomTypeId);

              const roomCount =
                this.availableRoomsPerRoomTypeId[roomType.RoomType.RoomTypeId];
              const hasRoomsAvailable = roomCount != null && roomCount > 0;

              const roomTypeOccupancyIsValid = utils.checkIfOccupancyIsValid(
                roomData.NumberOfAdults,
                roomData.NumberOfChildren,
                roomData.NumberOfInfants,
                roomType.RoomType.MaximumOccupancy,
                roomType.RoomType.MinimumOccupancy,
                roomType.RoomType.AdultMaximumOccupancy,
                roomType.RoomType.ChildMaximumOccupancy,
              );

              const ratePlanOccupanciesAreValid = validRatePlans.length > 0;

              return (
                roomTypeMatch &&
                hasRoomsAvailable &&
                roomTypeOccupancyIsValid &&
                ratePlanOccupanciesAreValid
              );
            },
          );

          return availableRoomTypes;
        }
        return [];
      },

      setRoomTypes(roomTypes: RoomTypeAvailability[]) {
        this.RoomTypes = roomTypes;
      },

      resetAvailableRoomsPerRoomType() {
        this.availableRoomsPerRoomTypeId = null;
      },

      initializeAvailableRoomsPerRoomType(
        numberOfRoomsPerRoomTypeId: Record<number, number>,
      ) {
        if (this.availableRoomsPerRoomTypeId == null) {
          this.availableRoomsPerRoomTypeId = {};
        }

        for (const key in numberOfRoomsPerRoomTypeId) {
          if (numberOfRoomsPerRoomTypeId.hasOwnProperty(key)) {
            const roomTypeId = key;
            const numberOfAvailableRooms = numberOfRoomsPerRoomTypeId[key];

            let obs = this.availableRoomsPerRoomTypeId[roomTypeId];
            if (!obs) {
              obs = numberOfAvailableRooms;
              this.availableRoomsPerRoomTypeId[roomTypeId] = obs;
            }
          }
        }
      },
      decreaseAvailabilityByOne(roomTypeId: number) {
        const a = this.getAvailability(roomTypeId);
        if (this.availableRoomsPerRoomTypeId) {
          this.availableRoomsPerRoomTypeId[roomTypeId] = a - 1;
        }
      },
      increaseAvailabilityByOne(roomTypeId: number) {
        const a = this.getAvailability(roomTypeId);
        if (this.availableRoomsPerRoomTypeId) {
          this.availableRoomsPerRoomTypeId[roomTypeId] = a + 1;
        }
      },
      updateAvailability(roomTypeId: number, newAvailability: number) {
        if (this.availableRoomsPerRoomTypeId) {
          this.availableRoomsPerRoomTypeId[roomTypeId] = newAvailability;
        }
      },
      getAvailability(roomTypeId: number): number | null {
        if (this.availableRoomsPerRoomTypeId) {
          const obs = this.availableRoomsPerRoomTypeId[roomTypeId];
          return obs ? obs : null;
        }
        return null;
      },
      showAvailabilityBadge(roomTypeId: number): boolean {
        const propertyStore = usePropertyStore();
        const showRoomTypeAvailabilityWarning = propertyStore.showRoomTypeAvailabilityWarning;

        if (this.availableRoomsPerRoomTypeId && showRoomTypeAvailabilityWarning) {
          const roomTypeAvailabilityWarningCount = propertyStore.roomTypeAvailabilityWarningCount;
          const obs = this.availableRoomsPerRoomTypeId[roomTypeId];
          return obs ? obs <= roomTypeAvailabilityWarningCount : false;
        }
        return false;
      },
    },
  },
);
